/**
 * DL-02 — the download manager's PURE state: a list of download items, one
 * running at a time (Christian 2026-10-22: "once after the other"), the rest
 * queued; stopped and done items stay listed until removed. No React, no I/O
 * — the provider feeds actions in and reads the next item to run out, the
 * unit spec pins each rule.
 *
 *   queued   waiting for its turn (its folder was picked at enqueue time)
 *   running  the ONE item `runImageDownload` is working on
 *   stopped  the run ended early: the user's Stop, disk full, a write failed
 *            — resumable (Resume re-queues it, the verify pass skips what is
 *            on disk)
 *   paused   "Pause all" (Christian 2026-10-22: a hotel line, come back
 *            later): queued items wait as paused, the running one finishes
 *            its files in flight and lands here too; "Resume all" re-queues
 *            them in their original order, "Stop all" turns them into stopped
 *   done     the run finished; `progress.failures` may be non-empty (Resume
 *            is offered then too)
 */
import type { DownloadDirLike } from "./fs.js";
import type { DownloadScope, ManifestBundle } from "./manifestApi.js";
import type { RunProgress } from "./run.js";

export type DownloadItemState =
  | "queued"
  | "running"
  | "paused"
  | "stopped"
  | "done";

export interface DownloadItem {
  id: string;
  scope: DownloadScope;
  bundle: ManifestBundle;
  root: DownloadDirLike;
  state: DownloadItemState;
  /** null until the run has reported once */
  progress: RunProgress | null;
  /** when the item was enqueued — the queue order */
  enqueuedAt: number;
}

export interface ManagerState {
  items: DownloadItem[];
  nextId: number;
  /** "Pause all" is on: nothing starts, the running item lands in paused */
  paused: boolean;
}

export const INITIAL_MANAGER_STATE: ManagerState = {
  items: [],
  nextId: 1,
  paused: false,
};

export type ManagerAction =
  | {
      type: "enqueue";
      scope: DownloadScope;
      bundle: ManifestBundle;
      root: DownloadDirLike;
      at: number;
    }
  | { type: "started"; id: string }
  | { type: "progress"; id: string; progress: RunProgress }
  | { type: "finished"; id: string; progress: RunProgress }
  | { type: "stop"; id: string }
  | { type: "resume"; id: string; at: number }
  | { type: "remove"; id: string }
  | { type: "clearDone" }
  | { type: "pauseAll" }
  | { type: "resumeAll" }
  | { type: "stopAll" };

/** The id the next `enqueue` will mint — the provider needs it to open the item view. */
export function nextItemId(state: ManagerState): string {
  return `dl-${String(state.nextId)}`;
}

export function managerReducer(
  state: ManagerState,
  action: ManagerAction,
): ManagerState {
  switch (action.type) {
    case "enqueue": {
      const id = nextItemId(state);
      return {
        ...state,
        nextId: state.nextId + 1,
        items: [
          ...state.items,
          {
            id,
            scope: action.scope,
            bundle: action.bundle,
            root: action.root,
            state: "queued",
            progress: null,
            enqueuedAt: action.at,
          },
        ],
      };
    }
    case "started":
      return patch(state, action.id, (it) =>
        it.state === "queued"
          ? { ...it, state: "running", progress: null }
          : it,
      );
    case "progress":
      return patch(state, action.id, (it) =>
        it.state === "running" ? { ...it, progress: action.progress } : it,
      );
    case "finished":
      return patch(state, action.id, (it) =>
        it.state === "running"
          ? {
              ...it,
              state:
                action.progress.phase === "stopped"
                  ? state.paused && action.progress.stop?.kind === "stopped"
                    ? "paused"
                    : "stopped"
                  : "done",
              progress: action.progress,
            }
          : it,
      );
    case "stop":
      // a queued item stops before it started: it simply leaves the queue as
      // "stopped" (resumable); a running one is flagged by the provider (the
      // engine's shouldStop) and lands in "stopped" through `finished`
      return patch(state, action.id, (it) =>
        it.state === "queued"
          ? { ...it, state: "stopped", progress: it.progress }
          : it,
      );
    case "resume":
      // a single paused item resumed by hand keeps its place in the order
      return patch(state, action.id, (it) =>
        it.state === "paused"
          ? { ...it, state: "queued" }
          : it.state === "stopped" || it.state === "done"
            ? { ...it, state: "queued", enqueuedAt: action.at }
            : it,
      );
    case "remove":
      return {
        ...state,
        items: state.items.filter((it) => it.id !== action.id),
      };
    case "clearDone":
      return {
        ...state,
        items: state.items.filter(
          (it) => !(it.state === "done" && it.progress?.failures.length === 0),
        ),
      };
    case "pauseAll":
      // queued → paused now; the running one is flagged by the provider and
      // lands in paused through `finished` (its files in flight complete)
      return {
        ...state,
        paused: true,
        items: state.items.map((it) =>
          it.state === "queued" ? { ...it, state: "paused" } : it,
        ),
      };
    case "resumeAll":
      return {
        ...state,
        paused: false,
        items: state.items.map((it) =>
          it.state === "paused" ? { ...it, state: "queued" } : it,
        ),
      };
    case "stopAll":
      // queued / paused → stopped (resumable one by one); the running one is
      // flagged by the provider and lands in stopped through `finished`
      return {
        ...state,
        paused: false,
        items: state.items.map((it) =>
          it.state === "queued" || it.state === "paused"
            ? { ...it, state: "stopped" }
            : it,
        ),
      };
  }
}

function patch(
  state: ManagerState,
  id: string,
  f: (it: DownloadItem) => DownloadItem,
): ManagerState {
  const items = state.items.map((it) => (it.id === id ? f(it) : it));
  const changed = items.some((it, k) => it !== state.items[k]);
  return changed ? { ...state, items } : state;
}

/** The item to start now: not paused, nothing running, the oldest queued one; else null. */
export function nextToRun(state: ManagerState): DownloadItem | null {
  if (state.paused) return null;
  if (state.items.some((it) => it.state === "running")) return null;
  const queued = state.items
    .filter((it) => it.state === "queued")
    .sort((a, b) => a.enqueuedAt - b.enqueuedAt);
  return queued[0] ?? null;
}

export interface ManagerAggregate {
  /** items running or queued */
  active: number;
  running: DownloadItem | null;
  /** items listed (not removed) */
  total: number;
  /** items done (with or without failures) */
  done: number;
  /** items stopped (user / disk full / write failed) */
  stopped: number;
  /** items waiting under "Pause all" (the pill never auto-hides while > 0) */
  paused: number;
  filesDone: number;
  filesTotal: number;
  bytesWritten: number;
}

/** The status bar's numbers over EVERY listed item (done ones included until removed). */
export function aggregate(state: ManagerState): ManagerAggregate {
  let active = 0;
  let done = 0;
  let stopped = 0;
  let paused = 0;
  let filesDone = 0;
  let filesTotal = 0;
  let bytesWritten = 0;
  let running: DownloadItem | null = null;
  for (const it of state.items) {
    if (it.state === "queued" || it.state === "running") active++;
    if (it.state === "running") running = it;
    if (it.state === "done") done++;
    if (it.state === "stopped") stopped++;
    if (it.state === "paused") paused++;
    filesTotal += it.progress?.filesTotal ?? it.bundle.imageCount;
    filesDone += it.progress?.filesDone ?? 0;
    bytesWritten += it.progress?.bytesWritten ?? 0;
  }
  return {
    active,
    running,
    total: state.items.length,
    done,
    stopped,
    paused,
    filesDone,
    filesTotal,
    bytesWritten,
  };
}
